import {
  Timeline,
  TimelineContent,
  TimelineDescription,
  TimelineIndicator,
  TimelineItem,
  TimelineTime,
  TimelineTitle,
} from "@diametral/design-system/react";

import { useProjectHistory } from "../../api/projects";
import type { HistoryEvent, ProjectDetail } from "../../api/types";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../../components/States";
import { formatDateTime } from "../../lib/format";
import { t, useTranslation } from "../../lib/i18n";
import { PROJECT_STATUS, historyActionLabel } from "../../lib/labels";
import { trlLabel } from "../../lib/reference";

/**
 * One readable line out of an audit event: the person and stream the backend names
 * (`staffing.project_history`), plus the details of `data` (shapes from `audit.record`).
 */
export function describeEvent(event: HistoryEvent): string {
  const data = event.data ?? {};
  const str = (value: unknown) => (typeof value === "string" ? value : "");
  const parts: string[] = [];
  if (event.person) parts.push(event.person);
  if (event.stream && !event.action.startsWith("stream.")) parts.push(t("stream “{{name}}”", { name: event.stream }));
  switch (event.action) {
    case "assignment.create":
      if (str(data.start_date)) parts.push(t("from {{date}}", { date: str(data.start_date) }));
      break;
    case "assignment.end":
      if (str(data.end_date)) parts.push(t("until {{date}}", { date: str(data.end_date) }));
      break;
    case "request.join":
    case "request.leave":
      if (!event.stream && event.action === "request.leave") parts.push(t("the whole project"));
      break;
    case "request.decide":
      parts.push(data.accept ? t("accepted") : t("refused"));
      if (str(data.message)) parts.push(t("“{{text}}”", { text: str(data.message) }));
      break;
    case "stream.archive":
      if (event.stream || str(data.title)) parts.push(t("“{{text}}”", { text: event.stream ?? str(data.title) }));
      break;
    case "project.status": {
      const status = str(data.status);
      if (status in PROJECT_STATUS)
        parts.push(t("New status: {{status}}", { status: PROJECT_STATUS[status as keyof typeof PROJECT_STATUS].label.toLowerCase() }));
      if (typeof data.ended_assignments === "number" && data.ended_assignments)
        parts.push(t("{{number}} assignment(s) ended", { number: data.ended_assignments }));
      if (typeof data.cancelled_requests === "number" && data.cancelled_requests)
        parts.push(t("{{number}} request(s) cancelled", { number: data.cancelled_requests }));
      break;
    }
    case "project.update":
      if (str(data.title)) parts.push(t("Title: {{title}}", { title: str(data.title) }));
      if (data.description) parts.push(t("Description changed"));
      if (typeof data.trl === "number") parts.push(`TRL ${data.trl} (${trlLabel(data.trl)})`);
      if (Array.isArray(data.technologies)) parts.push(t("Technologies: {{list}}", { list: data.technologies.join(", ") || t("none") }));
      break;
    case "project.create":
      if (str(data.title)) parts.push(str(data.title));
      break;
    case "project.repositories":
      if (Array.isArray(data.urls)) parts.push(data.urls.length ? data.urls.join(", ") : t("No link"));
      break;
    case "project.roles": {
      const count = (v: unknown) => (Array.isArray(v) ? v.length : 0);
      parts.push(`${count(data.tech_leads)} TL, ${count(data.product_owners)} PO`);
      break;
    }
    case "stream.create":
    case "stream.update":
      if (str(data.title) || event.stream) parts.push(t("“{{text}}”", { text: str(data.title) || event.stream }));
      break;
    default:
      break;
  }
  return parts.join(" · ");
}

export function HistoryTab({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const history = useProjectHistory(project.slug);
  if (history.isPending) return <LoadingBlock lines={5} label={t("Loading history…")} />;
  if (history.isError) return <ErrorBlock error={history.error} onRetry={() => void history.refetch()} />;
  if (!history.data.length) return <EmptyBlock icon="calendar" title={t("No event")} description={t("Changes to the project and its streams will appear here.")} />;
  return (
    <Timeline>
      {history.data.map((event, index) => {
        const detail = describeEvent(event);
        return (
          <TimelineItem key={`${event.at}-${index}`} tone={index === 0 ? "info" : undefined} data-state={index === 0 ? "active" : "completed"}>
            <TimelineIndicator />
            <TimelineContent>
              <TimelineTitle>{historyActionLabel(event.action)}</TimelineTitle>
              <TimelineTime dateTime={event.at}>
                {formatDateTime(event.at)}
                {event.actor ? ` · ${event.actor}` : ""}
              </TimelineTime>
              {detail ? <TimelineDescription>{detail}</TimelineDescription> : null}
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
}
